import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { Button } from '@/app/components/Button'
import { GuideSidebar } from '@/app/components/GuideSidebar'
import { GuideStickyShare } from '@/app/components/GuideStickyShare'
import PortableText from '@/app/components/PortableText'
import { SocialShare } from '@/app/components/SocialShare'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { Language } from '@/lang'
import { ContentSection as ContentSectionProps } from '@/sanity.types'

type ContentProps = {
  block: ContentSectionProps & { _key?: string }
  index: number
  language?: Language
  pageId?: string
  pageType?: string
}

export default function ContentSection({ block, language, pageId, pageType }: ContentProps) {
  if (!block?.content?.length) {
    return null
  }

  const blockStyle = stegaClean(block.style)
  const isGuide = pageType === 'guide'

  // Translation strings for social share
  const shareLabel = language === 'es' ? 'Compartir:' : 'Share:'
  const shareOnPrefix = language === 'es' ? 'Compartir en' : 'Share on'
  const linkCopiedLabel = language === 'es' ? '¡Enlace copiado!' : 'Link copied!'

  // Guide layout with sidebar
  if (isGuide) {
    return (
      <div className={cn('content-container my-4xl lg:my-5xl')}>
        <div className="mx-auto flex max-w-[1360px] flex-row gap-5xl">
          <GuideSidebar language={language}>
            <SocialShare
              className={language === 'es' ? 'px-2xl' : 'px-5xl'}
              label={shareLabel}
              shareOnPrefix={shareOnPrefix}
              linkCopiedLabel={linkCopiedLabel}
            />
            <div className="flex flex-col gap-lg p-4xl rounded-xl bg-neutral-50">
              <p className="size-subheading-lg font-bold text-center">
                Get free mobile data with Kite
              </p>
              <Button
                button={{
                  _type: 'button',
                  variant: 'secondary',
                  size: 'sm',
                  link: {
                    _type: 'link',
                    linkType: 'deviceDetectionDownload',
                    href: null,
                    internalPage: null,
                    internalPost: null,
                    external: null,
                    mailto: null,
                    tel: null,
                    anchor: null,
                    openInNewTab: null,
                    label: null,
                    ariaLabel: null,
                  },
                  icon: null,
                  iconPlacement: null,
                }}
                tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                className="self-center"
              >
                {language === 'es' ? 'Descargar' : 'Download Kite'}
              </Button>
            </div>
          </GuideSidebar>
          <div className="w-full min-w-0 flex-1">
            <div
              className={cn(
                'max-w-[1010px] mx-auto xl:mx-0',
                (blockStyle === 'neutral-50' || blockStyle === 'neutral-200') &&
                  'max-w-[434px] px-2xl py-lg lg:max-w-[1010px] lg:px-9xl lg:py-5xl',
                blockStyle === 'neutral-50' && 'rounded-lg bg-neutral-50',
                blockStyle === 'neutral-200' && 'rounded-md bg-neutral-200',
              )}
            >
              <PortableText
                value={block.content as PortableTextBlock[]}
                tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                documentId={pageId}
                documentType={pageType}
                parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
              />
            </div>
            <GuideStickyShare
              className="xl:hidden"
              shareLabel={shareLabel}
              shareOnPrefix={shareOnPrefix}
              linkCopiedLabel={linkCopiedLabel}
            />
          </div>
        </div>
      </div>
    )
  }

  // Default layout for non-guide pages
  return (
    <div className={cn('content-container my-4xl lg:my-5xl')}>
      <div
        className={cn(
          'mx-auto max-w-[1010px]',
          (blockStyle === 'neutral-50' || blockStyle === 'neutral-200') &&
            'max-w-[434px] px-2xl py-lg lg:max-w-[1010px] lg:px-9xl lg:py-5xl',
          blockStyle === 'neutral-50' && 'rounded-lg bg-neutral-50',
          blockStyle === 'neutral-200' && 'rounded-md bg-neutral-200',
        )}
      >
        <PortableText
          value={block.content as PortableTextBlock[]}
          tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
          documentId={pageId}
          documentType={pageType}
          parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
        />
      </div>
    </div>
  )
}
